import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { OnDestroy } from '@angular/core';
import {
  inject,
  Injectable,
  PLATFORM_ID,
  RendererFactory2,
} from '@angular/core';
import { ActivatedRoute } from '@freelancer/activated-route';
import { Auth } from '@freelancer/auth';
import { LocalStorage } from '@freelancer/local-storage';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { Observable } from 'rxjs';
import {
  combineLatest,
  firstValueFrom,
  fromEvent,
  of,
  Subscription,
} from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import type { Theme, ThemeSetting } from './theme.types';
import { ShellConfig } from '../../app/app-shell/shell-config.service';

@UntilDestroy({ className: 'ThemeService' })
@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  private document = inject(DOCUMENT);
  private localStorage = inject(LocalStorage);
  private platformId = inject(PLATFORM_ID);
  private renderer = inject(RendererFactory2).createRenderer(null, null);
  private activatedRoute = inject(ActivatedRoute);
  private auth = inject(Auth);
  private shellConfig = inject(ShellConfig);
  private uiConfig = inject(UI_CONFIG);

  private readonly DARK_MODE_MEDIA_QUERY = '(prefers-color-scheme: dark)';

  /**
   * themeSetting is the actual setting that the user has chosen (or been defaulted to).
   * This can be: 'light' | 'dark' | 'system'. This setting is stored in local storage.
   */
  private readonly themeSetting$: Observable<ThemeSetting>;
  /**
   * Theme is the actual theme being used in the app. This is either 'light' or 'dark'.
   * If the themeSetting is 'system', then the theme will be 'light' or 'dark' based on the user's OS settings.
   */
  private readonly theme$: Observable<Theme>;
  private subscription = new Subscription();

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.themeSetting$ = combineLatest([
        this.shellConfig.getConfig(this.activatedRoute.activatedRoute),
        this.auth.isLoggedIn(),
        this.localStorage.get('theme'),
      ]).pipe(
        switchMap(([config, isLoggedIn, theme]): Observable<ThemeSetting> => {
          // Check for specific themes first (priority over everything)
          if (
            this.uiConfig.theme === 'deloitte' ||
            this.uiConfig.theme === 'freightlancer'
          ) {
            return of('light');
          }

          if (config.forceDarkMode) {
            return of('dark');
          }

          // Allow user to override dark mode if they've selected a theme manually
          if (theme) {
            return of(theme); // If a user has logged in before but hasn't explicitly set their theme (so they currently are defaulting to light), is the localstorage item undefined.
          }

          if (!isLoggedIn) {
            return of('light');
          }

          return of('light');
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

      // Listen for changes to the system theme.
      this.subscription.add(
        fromEvent(
          window.matchMedia(this.DARK_MODE_MEDIA_QUERY),
          'change',
        ).subscribe(async event => {
          if (!('matches' in event)) {
            return;
          }

          const setting = await firstValueFrom(
            this.themeSetting$.pipe(untilDestroyed(this)),
          );

          if (setting === 'system') {
            this.applyThemeToDOM(event.matches ? 'dark' : 'light');
          }
        }),
      );

      // The actual theme being used in the app.
      // Derived from the themeSetting and the system theme.
      this.theme$ = this.themeSetting$.pipe(
        map(theme =>
          theme === 'system'
            ? window.matchMedia(this.DARK_MODE_MEDIA_QUERY).matches
              ? 'dark'
              : 'light'
            : theme,
        ),
      );
    } else {
      this.themeSetting$ = of('light');
      this.theme$ = of('light');
    }
  }

  /**
   * Return the current theme the user has selected.
   * This can be 'light' | 'dark' | 'system'.
   * You are probably looking for getTheme() instead.
   * This is to only be used when we need to know the user's actual setting.
   */
  getThemeSetting(): Observable<ThemeSetting> {
    return this.themeSetting$;
  }

  /**
   * Returns the current theme being set on the app.
   * If the themeSetting is 'system', then the theme will be 'light' or 'dark' based on the user's OS settings.
   * If the themeSetting is 'light' or 'dark', then the theme will be 'light' or 'dark' respectively.
   * Use this in the app code to get the current theme.
   */
  getTheme(): Observable<Theme> {
    return this.theme$;
  }

  async setThemeSetting(theme: ThemeSetting): Promise<void> {
    await this.localStorage.set('theme', theme);
  }

  /**
   * Applies the dark class to the DOM if the theme is dark.
   */
  private applyThemeToDOM(theme: Theme): void {
    if (isPlatformBrowser(this.platformId)) {
      const docElement = this.document.documentElement;
      if (theme === 'dark') {
        this.renderer.addClass(docElement, 'dark');
      } else {
        this.renderer.removeClass(docElement, 'dark');
      }
    }
  }

  applyTheme(theme: Theme): void {
    this.applyThemeToDOM(theme);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
